import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAzureAuth } from "@/hooks/useAzureAuth";
import { useUserStatus } from "@/hooks/useUserStatus";
import { ROUTE_PATH } from "@/constants/routeConstants";
import { AlertCircle, Mail, LogOut } from "lucide-react";

/**
 * Account Inactive Page
 * Displayed when user's account is inactive in the system or when there's an error
 * Provides contact information and logout option
 */
const AccountInactive: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAzureAuth();
  const { error, isLoading, isNoAgenciesError, isActive } = useUserStatus();
  
  // Get error from navigation state (for initial navigation) or from useUserStatus hook (persists on refresh)
  const state = location.state as { error?: string } | undefined;
  const errorMessage = error || state?.error || null;

  // Redirect to dashboard if user is actually active (prevents manual navigation)
  useEffect(() => {
    if (isActive === true) {
      navigate(ROUTE_PATH.ROUTE_DASHBOARD, { replace: true });
    }
  }, [isActive, navigate]);

  // Redirect to dashboard if this is a "no agencies" error
  useEffect(() => {
    if (isNoAgenciesError) {
      navigate(ROUTE_PATH.ROUTE_DASHBOARD, { replace: true });
    }
  }, [isNoAgenciesError, navigate]);

  const handleLogout = async () => {
    await logout('FORCED');
    navigate('/login');
  };

  // Show loader while API is being called
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            Verifying account status...
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
            Please wait while we check your account
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-[32rem] w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {errorMessage ? "Unable to Verify Account" : "Account Inactive"}
          </h1>

          {/* Message */}
          <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
            {errorMessage || "Your account is currently inactive and you cannot access the system at this time."}
          </p>

          {/* Contact Information for "User not authorized" error */}
          {errorMessage && errorMessage === "This user does not have access to ONEview" && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Need Help?
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Please contact Alera Health at{" "}
                    <a
                      href="mailto:support@alerahealth.com"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      support@alerahealth.com
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact Information - Only show if not an error */}
          {!errorMessage && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Need Help?
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Please contact your system administrator to reactivate your account.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Retry Button for Network Errors only */}
          {errorMessage && errorMessage === "Network Error" && (
            <button
              onClick={() => window.location.reload()}
              className="w-full mb-4 inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Retry
            </button>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>

          {/* Additional Information */}
          {!errorMessage && (
            <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
              If you believe this is an error, please contact support.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountInactive;

